/**
 * EarnRateGauge — Band-Anzeige der $/GB-Earn-Rate (Story 0.3, AC-1)
 *
 * Referenz: claude-design-import/dashboard.html (`renderGauge`) und
 * earn-rate.html. Umgesetzt ist die Band-Variante (die Story laesst
 * „Halbkreis ODER Band“ zu; der finale Design-Stand zeigt das Band).
 *
 * Skala: Floor (0,5× Basis) bis Cap (3,0× Basis). Farbtokens --rate-floor /
 * --rate-cap stammen aus Story 0.1.
 *
 * a11y-Auflage (Form-/Muster-Redundanz): Die drei Zonen sind NICHT nur ueber
 * Farbe unterscheidbar —
 *   - Floor-Zone: Diagonal-Schraffur (SVG-Pattern) + Icon + Textlabel
 *   - Mittelzone: ungemustert + Icon + Textlabel
 *   - Cap-Zone:  Punktraster (SVG-Pattern) + Icon + Textlabel
 * Zusaetzlich: <title>/<desc> im SVG, role="img" mit aria-labelledby und ein
 * Zonen-Chip (Icon + Text) fuer die aktuelle Einordnung des Account-Werts.
 */
import { useId } from 'react'
import { useTranslation } from 'react-i18next'
import { Minus, TrendingDown, TrendingUp } from 'lucide-react'
import { Tooltip } from './Tooltip'
import { clamp, formatMultiplier, formatNumber, formatRate } from './format'
import { earnRateZone, earnRateZoneBounds, type EarnRateZone } from './scale'
import './ui.css'

export type { EarnRateZone }

export type EarnRateGaugeProps = {
  /** Account-Durchschnitts-Rate ($/GB) — der Zeiger. */
  value: number
  /** Untere Skalengrenze (0,5×-Floor). */
  floor: number
  /** Obere Skalengrenze (3,0×-Cap). */
  cap: number
  /**
   * Basis-Rate (1,0×). Ohne Angabe aus floor/floorMultiplier abgeleitet
   * (Floor 0,050 bei 0,5× → Basis 0,100).
   */
  base?: number
  /** Aktuelle Netzwerkrate — optionale zusaetzliche Referenzlinie. */
  networkRate?: number
  floorMultiplier?: number
  capMultiplier?: number
  /** Einheit hinter den Zahlen, z. B. „$/GB“. */
  unit?: string
  decimals?: number
  className?: string
  'data-testid'?: string
}

const VIEW_W = 700
const VIEW_H = 108
const PAD_X = 52
const TRACK_Y = 46
const TRACK_H = 12

export function EarnRateGauge({
  value,
  floor,
  cap,
  base,
  networkRate,
  floorMultiplier = 0.5,
  capMultiplier = 3,
  unit = '$/GB',
  decimals = 3,
  className = '',
  'data-testid': testId = 'earn-rate-gauge',
}: EarnRateGaugeProps) {
  const { t } = useTranslation('ui')
  // useId() liefert IDs mit Doppelpunkten (":r0:"). Fuer SVG-`url(#…)`-
  // Referenzen und Test-Selektoren sind die unhandlich — daher entfernen.
  const reactId = useId().replace(/:/g, '')
  const hatchId = `ui-gauge-hatch-${reactId}`
  const dotsId = `ui-gauge-dots-${reactId}`
  const titleId = `ui-gauge-title-${reactId}`
  const descId = `ui-gauge-desc-${reactId}`

  // Degenerierte Skala (cap <= floor) faellt auf eine minimale Spanne zurueck,
  // damit keine Division durch 0 bzw. NaN-Koordinaten entstehen.
  const safeFloor = floor
  const safeCap = cap > floor ? cap : floor + 1
  const resolvedBase =
    base ?? (floorMultiplier > 0 ? safeFloor / floorMultiplier : (safeFloor + safeCap) / 2)
  const clampedBase = clamp(resolvedBase, safeFloor, safeCap)

  const scaleX = (v: number) =>
    PAD_X + ((clamp(v, safeFloor, safeCap) - safeFloor) / (safeCap - safeFloor)) * (VIEW_W - 2 * PAD_X)

  const { lowUpper, highLower } = earnRateZoneBounds(safeFloor, clampedBase, safeCap)
  const zone = earnRateZone(value, safeFloor, clampedBase, safeCap)
  const xLowEnd = scaleX(lowUpper)
  const xHighStart = scaleX(highLower)
  const xBase = scaleX(clampedBase)
  const xValue = scaleX(value)
  const trackStart = PAD_X
  const trackEnd = VIEW_W - PAD_X

  const outOfRange = value < safeFloor ? 'below' : value > safeCap ? 'above' : null

  const floorText = t('earnRateGauge.floorLabel', {
    multiplier: formatMultiplier(floorMultiplier),
    value: formatRate(safeFloor, unit, decimals),
  })
  const baseText = t('earnRateGauge.baseLabel', {
    multiplier: formatMultiplier(1),
    value: formatRate(clampedBase, unit, decimals),
  })
  const capText = t('earnRateGauge.capLabel', {
    multiplier: formatMultiplier(capMultiplier),
    value: formatRate(safeCap, unit, decimals),
  })
  const accountText = t('earnRateGauge.accountLabel', {
    value: formatRate(value, unit, decimals),
  })
  const zoneLabel = t(`earnRateGauge.zones.${zone}.label`)
  const zoneTooltip = t(`earnRateGauge.zones.${zone}.tooltip`, {
    multiplier: formatMultiplier(capMultiplier),
  })

  const ZoneIcon = zone === 'low' ? TrendingDown : zone === 'high' ? TrendingUp : Minus
  const zoneToneClass =
    zone === 'low' ? 'pill-danger' : zone === 'high' ? 'pill-success' : 'pill-info'

  return (
    <div className={`ui-gauge ${className}`.trim()} data-testid={testId} data-zone={zone}>
      <div className="ui-gauge__scroll">
        {/* Welle-2-Review F4: <title> benennt die Grafik (aria-labelledby),
            <desc> beschreibt sie (aria-describedby). Vorher hingen beide am
            Namen — Screenreader lasen den kompletten Muster-Erklaertext als
            Grafik-NAMEN vor. */}
        <svg
          className="ui-gauge__svg"
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          role="img"
          aria-labelledby={titleId}
          aria-describedby={descId}
          data-testid={`${testId}-svg`}
        >
          <title id={titleId}>
            {t('earnRateGauge.ariaLabel', {
              value: formatRate(value, unit, decimals),
              floor: formatRate(safeFloor, unit, decimals),
              base: formatRate(clampedBase, unit, decimals),
              cap: formatRate(safeCap, unit, decimals),
            })}
          </title>
          <desc id={descId}>{t('earnRateGauge.description')}</desc>

          <defs>
            {/* Muster-Redundanz: Schraffur = Floor-Zone, Punktraster = Cap-Zone. */}
            <pattern
              id={hatchId}
              width="8"
              height="8"
              patternUnits="userSpaceOnUse"
              patternTransform="rotate(45)"
            >
              <rect width="8" height="8" fill="var(--rate-floor)" fillOpacity="0.16" />
              <line x1="0" y1="0" x2="0" y2="8" stroke="var(--rate-floor)" strokeWidth="3" />
            </pattern>
            <pattern id={dotsId} width="8" height="8" patternUnits="userSpaceOnUse">
              <rect width="8" height="8" fill="var(--rate-cap)" fillOpacity="0.16" />
              <circle cx="4" cy="4" r="1.8" fill="var(--rate-cap)" />
            </pattern>
          </defs>

          {/* Track */}
          <rect
            x={trackStart}
            y={TRACK_Y}
            width={trackEnd - trackStart}
            height={TRACK_H}
            rx={TRACK_H / 2}
            fill="var(--surface-3)"
          />
          {/* Floor-Zone (Schraffur) */}
          <rect
            data-testid={`${testId}-zone-low`}
            x={trackStart}
            y={TRACK_Y}
            width={Math.max(0, xLowEnd - trackStart)}
            height={TRACK_H}
            fill={`url(#${hatchId})`}
          />
          {/* Cap-Zone (Punktraster) */}
          <rect
            data-testid={`${testId}-zone-high`}
            x={xHighStart}
            y={TRACK_Y}
            width={Math.max(0, trackEnd - xHighStart)}
            height={TRACK_H}
            fill={`url(#${dotsId})`}
          />

          {/* Basis-Marker (1,0×) */}
          <line
            data-testid={`${testId}-base-marker`}
            x1={xBase}
            x2={xBase}
            y1={TRACK_Y - 8}
            y2={TRACK_Y + TRACK_H + 8}
            stroke="var(--text-subtle)"
            strokeWidth="1.5"
            strokeDasharray="3 3"
          />

          {/* Netzwerkrate (optional) */}
          {typeof networkRate === 'number' && (
            <g data-testid={`${testId}-network-marker`}>
              <line
                x1={scaleX(networkRate)}
                x2={scaleX(networkRate)}
                y1={TRACK_Y - 4}
                y2={TRACK_Y + TRACK_H + 4}
                stroke="var(--info)"
                strokeWidth="2"
              />
              <text
                x={scaleX(networkRate)}
                y={TRACK_Y + TRACK_H + 22}
                textAnchor="middle"
                fontSize="10"
                fill="var(--info)"
                fontFamily="var(--font-mono)"
              >
                {t('earnRateGauge.networkLabel', {
                  value: formatNumber(networkRate, decimals),
                })}
              </text>
            </g>
          )}

          {/* Account-Zeiger: Kreis + Dreieck (Form-Redundanz zur Farbe) */}
          <g data-testid={`${testId}-pointer`} data-out-of-range={outOfRange ?? 'no'}>
            <polygon
              points={`${xValue - 7},${TRACK_Y - 10} ${xValue + 7},${TRACK_Y - 10} ${xValue},${TRACK_Y - 1}`}
              fill="var(--primary)"
            />
            <circle
              cx={xValue}
              cy={TRACK_Y + TRACK_H / 2}
              r="8"
              fill="var(--primary)"
              stroke="var(--surface)"
              strokeWidth="2.5"
            />
            <text
              x={clamp(xValue, PAD_X + 40, VIEW_W - PAD_X - 40)}
              y={TRACK_Y + TRACK_H + 38}
              textAnchor="middle"
              fontSize="11"
              fontWeight="600"
              fill="var(--primary)"
              fontFamily="var(--font-mono)"
            >
              {accountText}
            </text>
          </g>

          {/* Skalen-Endlabels */}
          <text
            x={trackStart}
            y={TRACK_Y - 14}
            textAnchor="start"
            fontSize="10"
            fill="var(--rate-floor)"
            fontFamily="var(--font-mono)"
          >
            {floorText}
          </text>
          <text
            x={trackEnd}
            y={TRACK_Y - 14}
            textAnchor="end"
            fontSize="10"
            fill="var(--rate-cap)"
            fontFamily="var(--font-mono)"
          >
            {capText}
          </text>
        </svg>
      </div>

      {/* Legende mit Icon + Text — die Zonen sind damit auch ohne Farbwahrnehmung
          benennbar; die Swatches wiederholen zusaetzlich das jeweilige Muster. */}
      <div className="ui-gauge__legend">
        <span className="ui-gauge__legend-item">
          <span
            className="ui-gauge__legend-swatch"
            aria-hidden="true"
            style={{
              background:
                'repeating-linear-gradient(45deg, var(--rate-floor) 0 3px, transparent 3px 6px)',
            }}
          />
          <TrendingDown aria-hidden="true" size={12} />
          {floorText}
        </span>
        <span className="ui-gauge__legend-item">
          <Minus aria-hidden="true" size={12} />
          {baseText}
        </span>
        <span className="ui-gauge__legend-item">
          <span
            className="ui-gauge__legend-swatch"
            aria-hidden="true"
            style={{
              background: 'radial-gradient(var(--rate-cap) 1.4px, transparent 1.5px) 0 0 / 6px 6px',
            }}
          />
          <TrendingUp aria-hidden="true" size={12} />
          {capText}
        </span>
        <Tooltip text={zoneTooltip} className="ui-gauge__zone">
          {(tipId) => (
            <span
              className={`pill ui-status-chip ${zoneToneClass}`}
              data-testid={`${testId}-zone-chip`}
              tabIndex={0}
              aria-describedby={tipId}
            >
              <ZoneIcon aria-hidden="true" size={13} className="ui-status-chip__icon" />
              {zoneLabel}
            </span>
          )}
        </Tooltip>
      </div>

      {outOfRange && (
        <p className="ui-sr-only" data-testid={`${testId}-out-of-range`} role="note">
          {t(`earnRateGauge.outOfRange.${outOfRange}`)}
        </p>
      )}
    </div>
  )
}
